import React, { useState, useEffect } from 'react';
import { PageWrap, Card, Skeleton } from '../components/UI';
import '../styles/AuditRequestDetail.css';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function getAuthHeaders() {
  try {
    const saved = localStorage.getItem("auditpros_user");
    if (saved) {
      const parsed  = JSON.parse(saved);
      const account = parsed?.account;
      const email   = account?.idTokenClaims?.email
        || account?.idTokenClaims?.preferred_username
        || account?.idTokenClaims?.emails?.[0]
        || account?.username || account?.preferred_username || account?.email;
      if (email) return { "x-user-email": email, "Content-Type": "application/json" };
    }
  } catch { /* ignore */ }
  return { "Content-Type": "application/json" };
}

const STEPPER_CONFIG = {
  submitted:       { label: 'Submitted',       color: 'var(--accent)',  bg: 'var(--blue-bg)',        step: 1 },
  in_process:      { label: 'In Process',      color: 'var(--purple)', bg: 'var(--purple-bg)',       step: 2 },
  ai_reviewed:     { label: 'AI Reviewed',     color: 'var(--cyan)',   bg: 'var(--cyan-bg)',         step: 3 },
  audit_completed: { label: 'Audit Completed', color: 'var(--green)',  bg: 'var(--green-bg)',        step: 4 },
  in_correction:   { label: 'In Correction',   color: 'var(--amber)',  bg: 'var(--amber-bg)',        step: 5 },
  closed:          { label: 'Closed',          color: 'var(--text2)',  bg: 'rgba(139,149,168,0.1)',  step: 6 },
};

const CA_STATUS_FLOW = [
  { status: 'in_process',      label: 'Mark as In Process',      color: 'var(--purple)' },
  { status: 'ai_reviewed',     label: 'Mark as AI Reviewed',     color: 'var(--cyan)'   },
  { status: 'audit_completed', label: 'Mark as Audit Completed', color: 'var(--green)'  },
  { status: 'in_correction',   label: 'Send for Correction',     color: 'var(--amber)'  },
  { status: 'closed',          label: 'Close Request',           color: 'var(--text2)'  },
];

function StatusBadge({ status }) {
  const c = STEPPER_CONFIG[status] || { label: status, color: 'var(--text2)', bg: 'rgba(139,149,168,0.1)' };
  return (
    <span className="ard-status-badge" style={{ background: c.bg, color: c.color }}>
      {c.label}
    </span>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="ard-info-row">
      <span className="ard-info-label">{label}</span>
      <span className="ard-info-value">{value}</span>
    </div>
  );
}

function ProgressStepper({ status }) {
  const steps = ['submitted', 'in_process', 'ai_reviewed', 'audit_completed', 'in_correction', 'closed'];
  const currentStep = STEPPER_CONFIG[status]?.step || 1;

  return (
    <div className="ard-stepper">
      {steps.map((s, i) => {
        const cfg    = STEPPER_CONFIG[s];
        const isDone = cfg.step < currentStep;
        const isCurr = cfg.step === currentStep;
        return (
          <React.Fragment key={s}>
            <div className="ard-step">
              <div
                className="ard-step-dot"
                style={{
                  background: isDone ? 'var(--green)' : isCurr ? cfg.color : 'var(--bg3)',
                  color:      isDone || isCurr ? '#fff' : 'var(--text3)',
                  border:     isCurr ? `2px solid ${cfg.color}` : '2px solid transparent',
                }}
              >
                {isDone ? '✓' : i + 1}
              </div>
              <div
                className="ard-step-label"
                style={{
                  color:      isCurr ? cfg.color : 'var(--text3)',
                  fontWeight: isCurr ? 600 : 400,
                }}
              >
                {cfg.label}
              </div>
            </div>
            {i < steps.length - 1 && (
              <div
                className="ard-step-line"
                style={{
                  background: STEPPER_CONFIG[steps[i + 1]]?.step <= currentStep ? 'var(--green)' : 'var(--border)',
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function AuditRequestDetail({ requestId, userRole, onBack, onOpenAudit }) {
  const [request,   setRequest]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [notes,     setNotes]     = useState('');
  const [updating,  setUpdating]  = useState(false);
  const [updateMsg, setUpdateMsg] = useState('');

  const isCA       = userRole?.isCA || false;
  const isCustomer = !isCA;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res  = await fetch(`${BASE}/audit-requests/${requestId}`, { headers: getAuthHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load');
        setRequest(data);
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    };
    load();
  }, [requestId]);

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true); setUpdateMsg('');
    try {
      const res  = await fetch(`${BASE}/audit-requests/${requestId}/status`, {
        method:  'PATCH',
        headers: getAuthHeaders(),
        body:    JSON.stringify({ status: newStatus, notes: notes || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update');
      setRequest(prev => ({ ...prev, status: newStatus }));
      setUpdateMsg(`Status updated to ${STEPPER_CONFIG[newStatus]?.label}`);
      setNotes('');
    } catch (err) { setError(err.message); }
    finally { setUpdating(false); }
  };

  const rNum = `AR${String(request?.request_number || 0).padStart(8, '0')}`;
  const docs  = Array.isArray(request?.document_urls) ? request.document_urls : [];

  return (
    <PageWrap>
      <button onClick={onBack} className="ard-back-btn">← Back to requests</button>

      {loading ? <Skeleton rows={8} /> : error ? (
        <div className="ard-error">{error}</div>
      ) : request && (
        <>
          <div className="ard-header">
            <div>
              <div className="ard-req-num">{rNum}</div>
              <h2 className="ard-title">{request.company_name}</h2>
              <div className="ard-period">Period: {request.period_from} – {request.period_to}</div>
            </div>
            <StatusBadge status={request.status} />
          </div>

          <ProgressStepper status={request.status} />

          <div className="ard-detail-grid">
            <Card>
              <div className="ard-card-title">Request Details</div>
              <div className="ard-card-body">
                <InfoRow label="Customer"  value={request.customer_name} />
                <InfoRow label="Email"     value={request.customer_email} />
                <InfoRow label="CA"        value={request.ca_name || '—'} />
                <InfoRow label="Submitted" value={new Date(request.submitted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} />
                <InfoRow label="Updated"   value={new Date(request.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} />
              </div>
            </Card>

            <Card>
              <div className="ard-card-title">Files</div>
              <div className="ard-card-body--padded">
                {request.tally_backup_url ? (
                  <a href={request.tally_backup_url} download className="ard-file-link">
                    📦 <span>Download Tally Backup</span>
                  </a>
                ) : (
                  <div className="ard-no-file">No Tally backup uploaded</div>
                )}
                {docs.length > 0 && (
                  <div className="ard-doc-list">
                    {docs.map((doc, i) => (
                      <a key={i} href={doc.url} target="_blank" rel="noreferrer" className="ard-file-link--sm">
                        📄 {doc.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {(request.ca_notes || request.customer_notes) && (
            <Card style={{ marginBottom: 16 }}>
              <div className="ard-card-title">Notes</div>
              <div className="ard-notes-body">
                {request.ca_notes && (
                  <div>
                    <div className="ard-note-label">CA Notes</div>
                    <div className="ard-note-text">{request.ca_notes}</div>
                  </div>
                )}
                {request.customer_notes && (
                  <div>
                    <div className="ard-note-label">Customer Notes</div>
                    <div className="ard-note-text">{request.customer_notes}</div>
                  </div>
                )}
              </div>
            </Card>
          )}

          <Card>
            <div className="ard-card-title">{isCA ? 'Update Status' : 'Respond'}</div>
            <div className="ard-card-body--padded">
              {updateMsg && (
                <div className="ard-update-success">✓ {updateMsg}</div>
              )}

              <div className="ard-textarea-wrap">
                <label className="ard-textarea-label">
                  {isCA ? 'Notes for customer (optional)' : 'Your response'}
                </label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  placeholder={isCA ? 'Add notes visible to the customer…' : 'Describe corrections made or any comments…'}
                  className="ard-textarea"
                />
              </div>

              {isCA && (
                <div className="ard-ca-actions">
                  {CA_STATUS_FLOW
                    .filter(a => a.status !== request.status)
                    .map(action => (
                      <button
                        key={action.status}
                        onClick={() => handleStatusUpdate(action.status)}
                        disabled={updating}
                        className="ard-btn-action"
                        style={{ background: action.color }}
                      >
                        {action.label}
                      </button>
                    ))
                  }
                </div>
              )}

              {isCustomer && request.status === 'in_correction' && (
                <button
                  onClick={() => handleStatusUpdate('closed')}
                  disabled={updating}
                  className="ard-btn-customer-close"
                >
                  {updating ? 'Submitting…' : 'Mark as Closed'}
                </button>
              )}

              {isCustomer && request.status !== 'in_correction' && (
                <div className="ard-customer-note">
                  {request.status === 'closed'
                    ? 'This request is closed.'
                    : 'Your CA is working on this request. You will be notified when action is needed.'}
                </div>
              )}
            </div>
          </Card>

          {request.sync_run_id && (
            <div className="ard-open-audit">
              <button onClick={() => onOpenAudit(request)} className="ard-btn-view-audit">
                View Audit Details →
              </button>
            </div>
          )}
        </>
      )}
    </PageWrap>
  );
}
